import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Smartphone, X, Eye, Clock, UserX, Flame } from "lucide-react";

export const ProblemSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      ref={ref}
      className="section-padding bg-gradient-to-b from-white to-mint-whisper relative overflow-hidden"
      data-testid="problem-section"
    >
      <div className="lumen-container">
        <div className="max-w-4xl mx-auto">
          {/* Instagram scenario */}
          <motion.div
            className="glass-card p-8 md:p-10 mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="icon-circle bg-stone-100 border-stone-200">
                <Smartphone className="w-5 h-5 text-stone-500" />
              </div>
              <div>
                <p className="font-manrope text-text-main text-lg leading-relaxed">
                  Hier, t'as ouvert Insta.
                </p>
                <p className="font-manrope text-text-main leading-relaxed mt-2">
                  Tu t'es dit <em>"je devrais poster"</em>.
                </p>
              </div>
            </div>
            
            <p className="font-manrope text-text-muted leading-relaxed mb-4 pl-16">
              Puis t'as regardé 3 contenus "IA" qui se ressemblent tous… <strong className="text-text-main">et t'as refermé.</strong>
            </p>
            
            <p className="font-manrope text-text-main leading-relaxed mb-6 pl-16">
              Parce que <strong className="text-emerald-700">tu refuses de sonner fake</strong>.
            </p>

            <div className="bg-stone-50 rounded-xl p-5 ml-16">
              <p className="font-manrope text-stone-600 leading-relaxed">
                Sauf qu'en face, le marché avance. Et toi… tu deviens <strong className="text-stone-800">encore plus invisible</strong>.
              </p>
            </div>
          </motion.div>

          {/* The truth */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="font-cormorant text-2xl md:text-3xl text-text-main mb-4">
              La vérité ?
            </h2>
            <p className="font-cormorant text-xl md:text-2xl text-emerald-700 font-medium">
              Le problème n'est pas l'IA. C'est comment on l'utilise.
            </p>
          </motion.div>

          {/* The overwhelm scenario */}
          <motion.div
            className="glass-card p-8 md:p-10 mb-16 border-amber-100"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="icon-circle bg-amber-50 border-amber-200">
                <Flame className="w-5 h-5 text-amber-600" />
              </div>
              <p className="font-manrope text-text-main text-lg leading-relaxed">
                On t'a vendu le rêve :
              </p>
            </div>
            
            <p className="font-manrope text-text-muted leading-relaxed mb-6 pl-16 italic">
              "Publie tous les jours. Fais une newsletter. Fais des stories. Crée des offres."
            </p>
            
            <p className="font-manrope text-text-main leading-relaxed mb-4 pl-16">
              Et toi tu te retrouves <strong>seul(e)</strong> : praticien(ne) + com' + copy + montage + SAV + compta.
            </p>

            <div className="bg-amber-50 rounded-xl p-5 ml-16">
              <p className="font-manrope text-amber-800 leading-relaxed">
                Résultat : tu tiens 10 jours… puis tu t'effondres. <strong>Et tu disparais.</strong>
              </p>
            </div>
          </motion.div>

          {/* LUMEN Solution teaser */}
          <motion.div
            className="text-center bg-gradient-to-br from-emerald-50 to-white rounded-3xl p-8 md:p-12 border border-emerald-100"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <h3 className="font-cormorant text-2xl md:text-3xl text-text-main mb-4">
              <span className="text-emerald-700 font-semibold">LUMEN</span>, c'est l'inverse.
            </h3>
            <p className="font-manrope text-lg text-text-main mb-6 max-w-xl mx-auto">
              Une <strong>team d'assistants IA</strong> (paiement unique) qui te rend <strong className="text-emerald-700">≈ 15h/semaine</strong>.
            </p>
            <p className="font-cormorant text-xl text-emerald-700 italic">
              Tu récupères du temps pour ton vrai métier : accompagner.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
